import { c as _c } from "react/compiler-runtime";
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { useKeyboard } from '@/hooks/keyboard/use-keyboard';
import { usePianoProfileState } from '@/hooks/store/use-piano-profile-store';
import { usePrintDate } from '@/hooks/use-print-date';
import { useTranslation } from '@/hooks/use-translation';

/**
 * The masthead of a printed sheet: what the document is on the left, which
 * instrument and when on the right.
 *
 * Hidden on screen — the page already says all of this in its own chrome,
 * which print hides.
 */
export const PrintHeader = t0 => {
  const $ = _c(36);
  const {
    title
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    brand,
    keyCount,
    model,
    serialNumber
  } = usePianoProfileState();
  const {
    keyboard
  } = useKeyboard();
  const printDate = usePrintDate();
  let t1;
  if ($[0] !== keyboard) {
    t1 = keyboard.getKeys();
    $[0] = keyboard;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const keys = t1;
  const range = keys.length > 0 ? `${keys[0].name}–${keys[keys.length - 1].name}` : null;
  let t2;
  if ($[2] !== serialNumber || $[3] !== t) {
    t2 = serialNumber ? t("S/N {{serialNumber}}", {
      ns: "navigation",
      serialNumber
    }) : null;
    $[2] = serialNumber;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== keyCount || $[6] !== t) {
    t3 = t("{{count}} keys", {
      count: keyCount,
      ns: "navigation"
    });
    $[5] = keyCount;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== range || $[9] !== t2 || $[10] !== t3) {
    t4 = [t2, t3, range].filter(Boolean);
    $[8] = range;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const specification = t4.join(" \xB7 ");
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="font-serif text-[15px]">Piano Touch <em className="italic">Lab</em></span>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== title) {
    t6 = <div className="flex flex-col gap-1">{t5}<h1 data-slot="print-title" className="m-0 font-serif text-[34px] leading-none">{title}</h1></div>;
    $[13] = title;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = brand && model ? " " : null;
  let t8;
  if ($[15] !== model) {
    t8 = model ? <em className="italic">{model}</em> : null;
    $[15] = model;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== brand || $[18] !== t7 || $[19] !== t8) {
    t9 = <span className="font-serif text-[18px]">{brand}{t7}{t8}</span>;
    $[17] = brand;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== specification) {
    t10 = <span className="font-mono text-xs text-muted-foreground">{specification}</span>;
    $[21] = specification;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== currentLanguage || $[24] !== printDate || $[25] !== t) {
    t11 = t("Printed on {{date}}", {
      date: format(printDate, "d MMMM yyyy", {
        locale: currentLanguage.startsWith("fr") ? fr : enUS
      })
    });
    $[23] = currentLanguage;
    $[24] = printDate;
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <span className="font-mono text-xs text-muted-foreground">{t11}</span>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t9) {
    t13 = <div className="flex flex-col items-end gap-[3px] text-[13px]">{t9}{t10}{t12}</div>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13 || $[34] !== t6) {
    t14 = <header className="ptl-print-only flex items-end justify-between gap-5 border-b-2 border-ptl-rule-strong pb-3.5">{t6}{t13}</header>;
    $[33] = t13;
    $[34] = t6;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};